import { v4 as uuidv4 } from 'uuid';
import { getSegmentsTrack } from '@goldcast/player-config/src/utils/playerConfigGetters';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import {
  ClipDeletes,
  CompositeSegment
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { mergeDeletes } from '@goldcast/player-config/src/utils/assetMetadataUtils';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import { Scene } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import { Segment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/Segment';
import { getClipFPS, getCompositeSegmentById } from '@goldcast/player-config/src/utils/clipUtils';
import { approxGT, approxGTE, approxLT, approxLTE } from '@goldcast/player-config/src/utils/compareUtils';
import { MIN_COMPOSITE_SEGMENT_DURATION } from './constants';
import { updateTransitionsWhenSegmentSplit } from './segmentTransitionUtils';
import { getSegmentAtCurrentFrame, getSegmentTimeFromPlayerTime } from '../timeUtils';
import {
  AddCompositeSegmentDeleteAction,
  AddCompositeSegmentDeleteWordsAction,
  RestoreCompositeSegmentDeleteAction
} from './types';
import { restoreDelete } from '@/components/molecules/Transcript/EditTranscriptMenu/utils';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
/**
 * @deprecated Use getCompositeSegmentById instead
 */
export const getClipCompositeSegment = (newClip: Clip, compositeId: string) => {
  return getCompositeSegmentById(newClip, compositeId);
};

export const addCompositeSegmentDelete = (
  compositeSegment: CompositeSegment,
  updateClipPayload: AddCompositeSegmentDeleteAction | AddCompositeSegmentDeleteWordsAction
) => {
  if (!compositeSegment.segment_metadata) {
    throw new Error('Composite segment metadata not found');
  }
  const { startTime, endTime } = updateClipPayload;

  const absoluteStartTime = Math.min(startTime, endTime);
  const absoluteEndTime = Math.max(startTime, endTime);

  const newDeletes: ClipDeletes = {
    ...(compositeSegment.segment_metadata.deletes || {}),
    [absoluteStartTime]: {
      bounds: [absoluteStartTime, absoluteEndTime],
      prev_end_time: absoluteStartTime,
      next_start_time: absoluteEndTime,
      deleteType: updateClipPayload.deleteType
    }
  };

  const mergeDeletesData = mergeDeletes(newDeletes);
  compositeSegment.segment_metadata.deletes = mergeDeletesData;

  if (
    approxLTE(absoluteStartTime, compositeSegment.segment_metadata.start) &&
    approxGTE(absoluteEndTime, compositeSegment.segment_metadata.start)
  ) {
    compositeSegment.segment_metadata.start = absoluteEndTime;
  }
  if (
    approxGTE(absoluteEndTime, compositeSegment.segment_metadata.end) &&
    approxLTE(absoluteStartTime, compositeSegment.segment_metadata.end)
  ) {
    compositeSegment.segment_metadata.end = absoluteStartTime;
  }
  return compositeSegment;
};

export const addSegmentDeletes = (clip: Clip, updateClipPayload: AddCompositeSegmentDeleteAction) => {
  const { startSegmentId, endSegmentId, startTime, endTime } = updateClipPayload;
  const playerConfig = clip.asset_metadata.player_config;
  if (!playerConfig) {
    throw new Error('Player config not found');
  }
  const segmentTracks = getSegmentsTrack(playerConfig);
  if (!segmentTracks) {
    throw new Error('Segments track not found');
  }
  const startSegment = segmentTracks.segments.find(segment => segment.id === startSegmentId);
  if (!startSegment) {
    throw new Error('Start segment not found');
  }
  const endSegment = segmentTracks.segments.find(segment => segment.id === endSegmentId);
  if (!endSegment) {
    throw new Error('End segment not found');
  }

  if (startSegmentId === endSegmentId) {
    if (startSegment.type !== SegmentType.COMPOSITE && startSegment.type !== SegmentType.VIDEO) {
      throw new Error('Deletes are only supported for composite and video segments');
    }
    if (!startSegment.segment_metadata) {
      throw new Error('Segment metadata not found');
    }
    const deletes = startSegment.segment_metadata?.deletes || {};
    const absoluteStartTime = Math.min(startTime, endTime);
    const absoluteEndTime = Math.max(startTime, endTime);

    const newDeletes: ClipDeletes = {
      ...(deletes || {}),
      [absoluteStartTime]: {
        bounds: [absoluteStartTime, absoluteEndTime],
        prev_end_time: absoluteStartTime,
        next_start_time: absoluteEndTime,
        deleteType: updateClipPayload.deleteType
      }
    };

    const mergeDeletesData = mergeDeletes(newDeletes);
    startSegment.segment_metadata.deletes = mergeDeletesData;
    if (approxLTE(absoluteStartTime, startSegment.segment_metadata.start)) {
      startSegment.segment_metadata.start = absoluteEndTime;
    }
    if (approxGTE(absoluteEndTime, startSegment.segment_metadata.end)) {
      startSegment.segment_metadata.end = absoluteStartTime;
    }
    return clip;
  }
  const startSegmentIndex = segmentTracks.segments.findIndex(segment => segment.id === startSegmentId);
  const endSegmentIndex = segmentTracks.segments.findIndex(segment => segment.id === endSegmentId);
  if (startSegmentIndex === -1 || endSegmentIndex === -1) {
    throw new Error('Start or end segment not found');
  }
  const firstSegment = segmentTracks.segments[startSegmentIndex];
  const lastSegment = segmentTracks.segments[endSegmentIndex];
  if (!firstSegment || !lastSegment) {
    throw new Error('First or last segment not found');
  }
  if (firstSegment.type !== SegmentType.COMPOSITE && firstSegment.type !== SegmentType.VIDEO) {
    throw new Error('First segment is not a composite or video segment');
  }

  if (lastSegment.type !== SegmentType.COMPOSITE && lastSegment.type !== SegmentType.VIDEO) {
    throw new Error('Last segment is not a composite or video segment');
  }

  if (firstSegment.type === SegmentType.COMPOSITE || firstSegment.type === SegmentType.VIDEO) {
    if (!firstSegment.segment_metadata) {
      throw new Error('Segment metadata not found');
    }
    const firstSegmentMaxVideoDuration = firstSegment.segment_metadata.maxVideoDuration;
    const firstSegmentDeletes = firstSegment.segment_metadata.deletes || {};
    const firstSegmentNewDeletes: ClipDeletes = {
      ...firstSegmentDeletes,
      [startTime]: {
        bounds: [startTime, firstSegmentMaxVideoDuration],
        prev_end_time: startTime,
        next_start_time: firstSegmentMaxVideoDuration,
        deleteType: updateClipPayload.deleteType
      }
    };
    const mergedFirstSegmentDeletes = mergeDeletes(firstSegmentNewDeletes);
    firstSegment.segment_metadata.deletes = mergedFirstSegmentDeletes;
    firstSegment.segment_metadata.end = startTime;
  }

  if (lastSegment.type === SegmentType.COMPOSITE || lastSegment.type === SegmentType.VIDEO) {
    if (!lastSegment.segment_metadata) {
      throw new Error('Segment metadata not found');
    }
    const lastSegmentDeletes = lastSegment.segment_metadata.deletes || {};
    const lastSegmentNewDeletes: ClipDeletes = {
      ...lastSegmentDeletes,
      '0': {
        bounds: [0, endTime],
        prev_end_time: 0,
        next_start_time: endTime,
        deleteType: updateClipPayload.deleteType
      }
    };
    const mergedLastSegmentDeletes = mergeDeletes(lastSegmentNewDeletes);
    lastSegment.segment_metadata.deletes = mergedLastSegmentDeletes;
    lastSegment.segment_metadata.start = endTime;
  }

  segmentTracks.segments = segmentTracks.segments.filter((segment, index) => {
    const isBoundary = index === startSegmentIndex || index === endSegmentIndex;
    const hasContent =
      (segment.type === SegmentType.VIDEO || segment.type === SegmentType.COMPOSITE) &&
      !!segment.segment_metadata &&
      approxLT(segment.segment_metadata.start, segment.segment_metadata.end);
    const isValidVideo = segment.type !== SegmentType.VIDEO || hasContent;
    const isValidComposite = segment.type !== SegmentType.COMPOSITE || !segment.segment_metadata || hasContent;

    return index < startSegmentIndex || index > endSegmentIndex || (isBoundary && isValidVideo && isValidComposite);
  });

  return clip;
};

/**
 * Splits a composite segment at the specified time into two separate segments.
 *
 * The function finds the composite segment at the given startTime, creates a clone with a new ID,
 * and splits the original segment at that point. It properly distributes deletes, scenes, and effects
 * between the two resulting segments based on their temporal bounds.
 *
 * @param clip - The clip containing the composite segment to split
 * @param startTime - The player time at which to split the composite segment
 * @throws {Error} If no segment is found at startTime, if the segment is not a composite segment,
 *                 if segment metadata is missing, or if player config/segments track is not found
 *
 * @remarks
 * - The original segment's end time is set to the split point
 * - The new segment's start time is set to the split point
 * - Deletes are split and adjusted for both segments, with a MANUAL delete added at the split point
 * - Scenes are split at frame boundaries if they span the split point
 * - Effects are filtered and adjusted based on their start time relative to the split point
 * - The function mutates the clip's player config by replacing the original segment with two segments
 */
export const splitCompositeSegment = (clip: Clip, startTime: number) => {
  const compositeSegment = getSegmentAtCurrentFrame(startTime, clip);
  if (!compositeSegment || compositeSegment.type !== SegmentType.COMPOSITE) {
    throw new Error('Segment at current time is not a composite segment');
  }
  const newCompositeSegment = structuredClone(compositeSegment);
  newCompositeSegment.id = uuidv4();
  if (!compositeSegment.segment_metadata || !newCompositeSegment.segment_metadata) {
    throw new Error('Composite segment metadata not found');
  }
  const compositeSegmentStartTime = getSegmentTimeFromPlayerTime(startTime, clip, compositeSegment.id);
  compositeSegment.segment_metadata.end = compositeSegmentStartTime;
  newCompositeSegment.segment_metadata.start = compositeSegmentStartTime;
  const fps = getClipFPS(clip);

  const { deletes: compositeSegmentDeletes, scenes: compositeSegmentScenes } = compositeSegment.segment_metadata;
  compositeSegment.segment_metadata.deletes = mergeDeletes(
    Object.entries(compositeSegmentDeletes).reduce(
      (acc, [key, value]) => {
        if (approxLT(value.bounds[1], compositeSegmentStartTime)) {
          acc[key] = value;
        } else if (approxLT(value.bounds[0], compositeSegmentStartTime)) {
          acc[key] = {
            ...value,
            bounds: [value.bounds[0], compositeSegmentStartTime],
            prev_end_time: value.bounds[0],
            next_start_time: compositeSegmentStartTime
          };
        }
        return acc;
      },
      {
        [compositeSegmentStartTime]: {
          bounds: [compositeSegmentStartTime, compositeSegment.maxBounds.toFrame / fps],
          prev_end_time: compositeSegmentStartTime,
          next_start_time: compositeSegment.maxBounds.toFrame / fps,
          deleteType: 'MANUAL'
        }
      } as ClipDeletes
    )
  );

  compositeSegment.segment_metadata.scenes = compositeSegmentScenes.reduce((acc, scene) => {
    if (approxLT(scene.toFrame, compositeSegmentStartTime * fps)) {
      acc.push(scene);
    } else if (approxLT(scene.fromFrame, compositeSegmentStartTime * fps)) {
      acc.push({
        ...scene,
        toFrame: compositeSegmentStartTime * fps
      });
    }
    return acc;
  }, [] as Scene[]);
  compositeSegment.effects = compositeSegment.effects?.filter(effect =>
    approxLT(effect.start, compositeSegmentStartTime)
  );
  const effectToBeSplit = compositeSegment.effects?.find(effect =>
    approxGT(effect.start + effect.duration, compositeSegmentStartTime)
  );
  if (effectToBeSplit) {
    effectToBeSplit.duration = compositeSegmentStartTime - effectToBeSplit.start;
  }

  newCompositeSegment.effects = newCompositeSegment.effects?.filter(effect =>
    approxGT(effect.start, compositeSegmentStartTime)
  );

  const { deletes: newCompositeSegmentDeletes, scenes: newCompositeSegmentScenes } =
    newCompositeSegment.segment_metadata;
  newCompositeSegment.segment_metadata.deletes = mergeDeletes(
    Object.entries(newCompositeSegmentDeletes).reduce(
      (acc, [key, value]) => {
        if (approxGT(value.bounds[0], compositeSegmentStartTime)) {
          acc[key] = value;
        } else if (approxGT(value.bounds[1], compositeSegmentStartTime)) {
          acc[key] = {
            ...value,
            bounds: [compositeSegmentStartTime, value.bounds[1]],
            prev_end_time: compositeSegmentStartTime,
            next_start_time: value.bounds[1]
          };
        }
        return acc;
      },
      {
        '0': {
          bounds: [0, compositeSegmentStartTime],
          prev_end_time: 0,
          next_start_time: compositeSegmentStartTime,
          deleteType: 'MANUAL'
        }
      } as ClipDeletes
    )
  );

  newCompositeSegment.segment_metadata.scenes = newCompositeSegmentScenes.reduce((acc, scene) => {
    if (approxGT(scene.fromFrame, compositeSegmentStartTime * fps)) {
      acc.push(scene);
    } else if (approxGT(scene.toFrame, compositeSegmentStartTime * fps)) {
      acc.push({
        ...scene,
        fromFrame: compositeSegmentStartTime * fps
      });
    }
    return acc;
  }, [] as Scene[]);

  // Adjusting the end of the left due to boundary deletes.
  const endBoundaryDelete = Object.values(compositeSegment.segment_metadata.deletes).find(
    d => approxLTE(d.bounds[0], compositeSegmentStartTime) && approxGTE(d.bounds[1], compositeSegmentStartTime)
  );
  if (endBoundaryDelete) {
    compositeSegment.segment_metadata.end = endBoundaryDelete.bounds[0];
  }

  // Adjusting the start of the right segment due to boundary deletes.
  const startBoundaryDelete = Object.values(newCompositeSegment.segment_metadata.deletes).find(
    d => approxLTE(d.bounds[0], compositeSegmentStartTime) && approxGTE(d.bounds[1], compositeSegmentStartTime)
  );
  if (startBoundaryDelete) {
    newCompositeSegment.segment_metadata.start = startBoundaryDelete.bounds[1];
  }

  const playerConfig = clip.asset_metadata.player_config;
  if (!playerConfig) {
    throw new Error('Player config not found');
  }
  const segmentsTrack = getSegmentsTrack(playerConfig);
  if (!segmentsTrack) {
    throw new Error('Segments track not found');
  }
  segmentsTrack.segments = segmentsTrack.segments.reduce((acc, segment) => {
    if (segment.id === compositeSegment.id) {
      acc.push(compositeSegment);
      acc.push(newCompositeSegment);
    } else {
      acc.push(segment);
    }
    return acc;
  }, [] as Segment[]);

  if (featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Segment_Transitions]) {
    updateTransitionsWhenSegmentSplit(clip, compositeSegment.id, newCompositeSegment.id);
  }
};

export const splitOtherSegments = (clip: Clip, startTime: number) => {
  const oldSegment = getSegmentAtCurrentFrame(startTime, clip);
  const newSegment = structuredClone(oldSegment);

  if (!oldSegment || !newSegment) {
    throw new Error('Segment at current time not found');
  }
  if (oldSegment.type !== SegmentType.VIDEO || newSegment.type !== SegmentType.VIDEO) {
    throw new Error('Segment at current time is not a video segment');
  }
  if (!oldSegment.segment_metadata || !newSegment.segment_metadata || !oldSegment.currentBounds) {
    throw new Error('Segment metadata not found');
  }
  newSegment.id = uuidv4();
  const fps = getClipFPS(clip);
  const segmentTrimmedStartTime = oldSegment.currentBounds.fromFrame / fps;
  const segmentStartTime = oldSegment.fromFrame / fps;

  const segmentSplitTime = segmentTrimmedStartTime + (startTime - segmentStartTime);

  newSegment.segment_metadata.start = segmentSplitTime;
  oldSegment.segment_metadata.end = segmentSplitTime;

  const { deletes: oldSegmentDeletes } = oldSegment.segment_metadata;
  const { deletes: newSegmentDeletes } = newSegment.segment_metadata;

  oldSegment.segment_metadata.deletes = Object.entries(oldSegmentDeletes).reduce(
    (acc, [key, value]) => {
      if (approxLT(value.bounds[1], segmentSplitTime)) {
        acc[key] = value;
      } else if (approxLT(value.bounds[0], segmentSplitTime)) {
        acc[key] = {
          ...value,
          bounds: [value.bounds[0], segmentSplitTime],
          prev_end_time: value.bounds[0],
          next_start_time: segmentSplitTime
        };
      }
      return acc;
    },
    {
      [segmentSplitTime]: {
        bounds: [segmentSplitTime, (oldSegment.maxBounds?.toFrame || 0) / fps],
        prev_end_time: segmentSplitTime,
        next_start_time: (oldSegment.maxBounds?.toFrame || 0) / fps,
        deleteType: 'MANUAL'
      }
    } as ClipDeletes
  );

  newSegment.segment_metadata.deletes = Object.entries(newSegmentDeletes).reduce(
    (acc, [key, value]) => {
      if (approxGT(value.bounds[0], segmentSplitTime)) {
        acc[key] = value;
      } else if (approxGT(value.bounds[1], segmentSplitTime)) {
        acc[key] = {
          ...value,
          bounds: [segmentSplitTime, value.bounds[1]],
          prev_end_time: segmentSplitTime,
          next_start_time: value.bounds[1]
        };
      }
      return acc;
    },
    {
      '0': {
        bounds: [0, segmentSplitTime],
        prev_end_time: 0,
        next_start_time: segmentSplitTime,
        deleteType: 'MANUAL'
      }
    } as ClipDeletes
  );

  const playerConfig = clip.asset_metadata.player_config;
  if (!playerConfig) {
    throw new Error('Player config not found');
  }
  const segmentsTrack = getSegmentsTrack(playerConfig);
  if (!segmentsTrack) {
    throw new Error('Segments track not found');
  }
  segmentsTrack.segments = segmentsTrack.segments.reduce((acc, segment) => {
    if (segment.id === oldSegment.id) {
      acc.push(oldSegment);
      acc.push(newSegment);
    } else {
      acc.push(segment);
    }
    return acc;
  }, [] as Segment[]);

  if (featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Segment_Transitions]) {
    updateTransitionsWhenSegmentSplit(clip, oldSegment.id, newSegment.id);
  }
};

export const splitSegment = (clip: Clip, startTime: number) => {
  const segment = getSegmentAtCurrentFrame(startTime, clip);
  const isCompositeSegment = segment?.type === SegmentType.COMPOSITE;
  if (isCompositeSegment) {
    splitCompositeSegment(clip, startTime);
  } else {
    splitOtherSegments(clip, startTime);
  }
  return clip;
};

export const addSegmentRestore = (clip: Clip, updateClipPayload: RestoreCompositeSegmentDeleteAction) => {
  const { startTime, endTime, startCompositeId, endCompositeId } = updateClipPayload;
  const playerConfig = clip.asset_metadata.player_config;
  if (!playerConfig) {
    throw new Error('Player config not found');
  }
  const segmentsTrack = getSegmentsTrack(playerConfig);
  if (!segmentsTrack) {
    throw new Error('Segments track not found');
  }
  const startSegment = segmentsTrack.segments.find(segment => segment.id === startCompositeId);
  if (!startSegment) {
    throw new Error('Start segment not found');
  }
  const endSegment = segmentsTrack.segments.find(segment => segment.id === endCompositeId);
  if (!endSegment) {
    throw new Error('End segment not found');
  }

  if (startSegment.type !== SegmentType.COMPOSITE || endSegment.type !== SegmentType.COMPOSITE) {
    return;
  }
  if (!startSegment.segment_metadata || !endSegment.segment_metadata) {
    throw new Error('Segment metadata not found');
  }

  if (startCompositeId === endCompositeId) {
    startSegment.segment_metadata.deletes = restoreDelete(startSegment.segment_metadata.deletes || {}, {
      bounds: [startTime, endTime],
      next_start_time: endTime,
      prev_end_time: startTime
    });
    if (approxLTE(startTime, startSegment.segment_metadata.start)) {
      startSegment.segment_metadata.start = startTime;
    }
    if (approxGTE(endTime, startSegment.segment_metadata.end)) {
      startSegment.segment_metadata.end = endTime;
    }
  } else {
    const startSegmentEndTime = startSegment.segment_metadata.end;
    startSegment.segment_metadata.deletes = restoreDelete(startSegment.segment_metadata.deletes || {}, {
      bounds: [startTime, startSegmentEndTime],
      next_start_time: startSegmentEndTime,
      prev_end_time: startTime
    });

    const endSegmentStartTime = endSegment.segment_metadata.start;
    endSegment.segment_metadata.deletes = restoreDelete(endSegment.segment_metadata.deletes || {}, {
      bounds: [endSegmentStartTime, endTime],
      next_start_time: endTime,
      prev_end_time: endSegmentStartTime
    });
  }

  const startSegmentIndex = segmentsTrack.segments.findIndex(segment => segment.id === startCompositeId);
  const endSegmentIndex = segmentsTrack.segments.findIndex(segment => segment.id === endCompositeId);
  if (startSegmentIndex === -1 || endSegmentIndex === -1) {
    // not possible to happen
    return;
  }
  const allBetweenSegments = segmentsTrack.segments.slice(startSegmentIndex + 1, endSegmentIndex);
  allBetweenSegments.forEach(segment => {
    if (segment.type !== SegmentType.COMPOSITE || !segment.segment_metadata) {
      return;
    }
    segment.segment_metadata.deletes = restoreDelete(segment.segment_metadata.deletes || {}, {
      bounds: [segment.segment_metadata.start, segment.segment_metadata.end],
      next_start_time: segment.segment_metadata.end,
      prev_end_time: segment.segment_metadata.start
    });
  });
  return clip;
};

// Deletes can produce very small composite segments that are too short to be edited on the timeline
export const filterVerySmallComposites = (clip: Clip) => {
  const playerConfig = clip.asset_metadata.player_config;
  if (!playerConfig) {
    throw new Error('Player config not found');
  }
  const segmentsTrack = getSegmentsTrack(playerConfig);
  if (!segmentsTrack) {
    throw new Error('Segments track not found');
  }

  segmentsTrack.segments = segmentsTrack.segments.filter(segment => {
    if (segment.type !== SegmentType.COMPOSITE) {
      return true;
    }
    if (!segment.segment_metadata) {
      return false;
    }
    return (
      segment.segment_metadata.end - segment.segment_metadata.start > MIN_COMPOSITE_SEGMENT_DURATION &&
      segment.segment_metadata.duration > MIN_COMPOSITE_SEGMENT_DURATION
    );
  });

  const remainingComposites = segmentsTrack.segments.filter(segment => segment.type === SegmentType.COMPOSITE);
  if (remainingComposites.length < 1) {
    throw new Error('No composite segments remaining after filtering');
  }

  return clip;
};
